import type { NetworkData } from '@dynamic-labs-sdk/client';
import { FeeEstimationFailedError } from '@dynamic-labs-sdk/client';
import { formatEther } from 'viem';

import type {
  EvmTransactionFeeData,
  EvmTransactionToSimulate,
} from '../../transactionSimulation.types';
import { createPublicClientFromNetworkData } from '../../viem/createPublicClientFromNetworkData';

type CalculateEvmTransactionFeeParams = {
  nativeTokenPriceUsd?: number;
  networkData: NetworkData;
  transaction: EvmTransactionToSimulate;
};

/**
 * Calculates the estimated fee for an EVM transaction.
 *
 * This function estimates gas usage and calculates the total transaction fee
 * using either legacy gas pricing or EIP-1559 pricing. The fee is returned
 * in both native units (wei) and human-readable format (ETH).
 *
 * @param params.transaction - The transaction to estimate fees for.
 * @param params.networkData - Network configuration for RPC connection.
 * @param params.nativeTokenPriceUsd - Optional USD price of the native token for fee conversion.
 * @returns Fee data including gas estimates and human-readable amounts.
 * @throws {FeeEstimationFailedError} If gas estimation or fee retrieval fails.
 */
export const calculateEvmTransactionFee = async ({
  transaction,
  networkData,
  nativeTokenPriceUsd,
}: CalculateEvmTransactionFeeParams): Promise<EvmTransactionFeeData> => {
  try {
    const publicClient = createPublicClientFromNetworkData({ networkData });

    // Estimate gas for the transaction
    const gasEstimate = await publicClient.estimateGas({
      account: transaction.from,
      data: transaction.data,
      to: transaction.to,
      value: transaction.value,
    });

    // Try to get EIP-1559 fees first, fallback to legacy gas price
    let maxFeePerGas: bigint | undefined;
    let maxPriorityFeePerGas: bigint | undefined;
    let gasPrice: bigint | undefined;
    let nativeAmount: bigint;

    try {
      const feeData = await publicClient.estimateFeesPerGas();
      maxFeePerGas = feeData.maxFeePerGas;
      maxPriorityFeePerGas = feeData.maxPriorityFeePerGas;

      // Calculate fee using EIP-1559 pricing
      nativeAmount = gasEstimate * maxFeePerGas;
    } catch {
      // Fallback to legacy gas pricing
      gasPrice = await publicClient.getGasPrice();
      nativeAmount = gasEstimate * gasPrice;
    }

    // Format to human-readable ETH
    const humanReadableAmount = formatEther(nativeAmount);

    // Calculate USD amount if price data available
    const usdAmount = nativeTokenPriceUsd
      ? (Number(humanReadableAmount) * nativeTokenPriceUsd).toFixed(2)
      : undefined;

    return {
      gasEstimate,
      gasPrice,
      humanReadableAmount,
      maxFeePerGas,
      maxPriorityFeePerGas,
      nativeAmount,
      usdAmount,
    };
  } catch (error) {
    throw new FeeEstimationFailedError({
      cause: error instanceof Error ? error : undefined,
      message: 'Failed to estimate EVM transaction fee',
    });
  }
};
